import React from "react";
import { useFormikContext } from "formik";
import { Box, Button, Stack, Text } from "@origin-digital/ods-core";
import { IconError, IconPlus } from "@origin-digital/ods-icons";
import BulkDuplicatePremisesRow from "./BulkDuplicatePremisesRow";
import { getFieldsDataFromAddress } from "../helpers";
import {
  BulkDuplicateFormValues,
  BulkDuplicatePremises,
  PremiseWithError,
} from "./types";
import { validateBulkDuplicatePremisesPerRow } from "src/components/Form/FormComponents/Validations";
import { useBulkDuplicateActions } from "./hooks/useBulkDuplicateActions";

export const BulkDuplicatePremisesSelection = () => {
  const { values, setFieldValue, /* setErrors, */ errors } =
    useFormikContext<BulkDuplicateFormValues>();
  const { checkDuplicateInDB } = useBulkDuplicateActions();
  const [isAddingPremise, setIsAddingPremise] = React.useState(false);

  React.useEffect(() => {
    if (values.premises.length > 0) {
      const lastIndex = values.premises.length - 1;

      const fieldOrder = [
        "dp_number",
        "unit_number",
        "floor_number",
        "section_number",
        "lot_number",
        "street_number",
      ];

      const firstSelectedField = fieldOrder.find((field) =>
        values.selectedAddressParts.includes(field)
      );

      if (firstSelectedField) {
        const firstField = document.getElementById(
          `${firstSelectedField}_${lastIndex}`
        ) as HTMLInputElement;

        if (firstField) {
          setTimeout(() => {
            firstField.focus();
          }, 100);
        }
      }
    }
  }, [values.premises.length]);

  const handleAddMorePremises =
    (/* e: React.MouseEvent<HTMLButtonElement> */) => {
      /* e.preventDefault();
    e.stopPropagation(); */
      setIsAddingPremise(true);
      const newPremise = Object.keys(values.addressParts).reduce(
        (acc, key) => {
          acc[key as keyof BulkDuplicatePremises] =
            values.selectedAddressParts.includes(key)
              ? ""
              : getFieldsDataFromAddress(values.addressObj, key);
          return acc;
        },
        { hasError: false } as PremiseWithError
      );
      const updatedPremises = [...values.premises, { ...newPremise }];
      setTimeout(() => {
        //if(values.premises.length > 2){
        handleBulkDuplicatePremisesValidation(
          updatedPremises.length - 2,
          updatedPremises
        );
        //console.log("if condition premises", updatedPremises);
        /* } else {
        console.log("else condition premises", updatedPremises);
        setFieldValue("premises", updatedPremises); */
        //}
        //setFieldValue("premises", updatedPremises);
        setIsAddingPremise(false);
      }, 100);
    };

  const handleBulkDuplicatePremisesValidation = async (
    index: number,
    newpremises?: PremiseWithError[]
  ) => {
    if (index < 0) {
      setFieldValue("premises", newpremises);
      return;
    }
    const premises = newpremises || values.premises;
    const {
      isValid,
      errors: validationErrors,
      fieldErrors,
    } = validateBulkDuplicatePremisesPerRow(
      premises,
      values.selectedAddressParts,
      values.addressObj,
      index
    );
    if (isValid) {
      const updatedPremise = await checkDuplicateInDB(premises[index]);
      const updatedPremises = [...premises];
      updatedPremises[index] = { ...updatedPremises[index], ...updatedPremise };
      setFieldValue("premises", updatedPremises);
    } else {
      const updatedPremises = [...premises];
      updatedPremises[index] = {
        ...updatedPremises[index],
        hasError: !isValid,
        fieldErrors: fieldErrors[index] || false,
        errors: validationErrors[index] || {},
      };
      setFieldValue("premises", updatedPremises);
    }
  };

  return (
    <Stack space="medium">
      <Text>What are the addresses of the new premises?</Text>

      <Box borderRadius="medium" overflow="auto">
        <Box>
          {/* Row */}
          {(values.premises || []).map((premise, index) => (
            <BulkDuplicatePremisesRow
              key={index}
              premise={premise}
              index={index}
              handleBulkDuplicatePremisesValidation={
                handleBulkDuplicatePremisesValidation
              }
              isAddingPremise={isAddingPremise}
            />
          ))}
          {/* End Row */}
          {/* Add button row */}
          <Box
            padding="medium"
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            gap="xsmall"
            backgroundColor="grey50"
          >
            {values.premises.length === 0 && (
              <Box marginBottom="medium">
                <Text tone="neutralLight" align="center">
                  You haven't added any premises yet.
                </Text>
              </Box>
            )}
            <Button
              variant="outlined"
              color="secondaryB"
              size="small"
              icon={<IconPlus color="inherit" />}
              disabled={values.premises.length === 20}
              onClick={handleAddMorePremises}
              data-id="add-premises-button"
            >
              Add premises
            </Button>
            {values.premises.length === 20 && (
              <Text variant="body-small" tone="neutralLight">
                You’ve reached the maximum of 20 premises.
              </Text>
            )}
          </Box>
          {/* End Add button row */}
        </Box>
      </Box>
      {/* No premise added error */}
      {errors.premises && (
        <Box
          display="flex"
          alignItems={{ xs: "flex-start", md: "center" }}
          gap="xxsmall"
          paddingTop="none"
        >
          <IconError color="critical" size="xsmall" variant="solid" />
          <Text variant="body-small" tone="critical">
            Oops, you need to add at least one premise.
          </Text>
        </Box>
      )}
      {/* End No premise added error */}
    </Stack>
  );
};
